import { AlertColor } from '@mui/material/Alert/Alert'
import { AxiosError } from 'axios'
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useMemo,
  useState
} from 'react'
import { QueryObserverResult } from 'react-query'
import { useBoolean } from 'react-use'

import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { AWSIAMRoleMetadata } from 'types/api/aws/metadata'
import { EndpointResponse } from 'types/api/status-checks/status-check'

export const INITIAL_AUTHENTICATION_RESPONSE: AuthenticationResponse = {
  alertSeverity: null,
  httpStatusCode: null,
  responseBody: '',
  message: ''
}

export interface AuthenticationResponse extends EndpointResponse {
  alertSeverity: AlertColor
  message: string
}

interface ObservabilityToolAuthenticationContextValues {
  authenticationResponse: AuthenticationResponse
  setAuthenticationResponse: Dispatch<SetStateAction<AuthenticationResponse>>
  awsIAMRoleMetadata?: AWSIAMRoleMetadata
  isFetchingAwsIAMRoleMetadata?: boolean
  hasErrorAwsIAMRoleMetadata?: boolean
  awsIAMRoleMetadataError?: AxiosError | null
  reFetchAwsIAMRoleMetadata?: () => Promise<
    QueryObserverResult<AWSIAMRoleMetadata, AxiosError>
  >
  isCreatingAuthenticationOnly?: boolean
  setIsCreatingAuthenticationOnly?: Dispatch<SetStateAction<boolean>>
  isEdit?: boolean
  setIsEdit?: Dispatch<SetStateAction<boolean>>
  isOnboarding?: boolean
  setIsOnboarding?: Dispatch<SetStateAction<boolean>>
  isDrawerFlow?: boolean
}

const DEFAULT_OBSERVABILITY_TOOL_AUTHENTICATION_VALUES: ObservabilityToolAuthenticationContextValues =
  {
    authenticationResponse: INITIAL_AUTHENTICATION_RESPONSE,
    setAuthenticationResponse: () => {}
  }

export const ObservabilityToolAuthenticationContext =
  createContext<ObservabilityToolAuthenticationContextValues>(
    DEFAULT_OBSERVABILITY_TOOL_AUTHENTICATION_VALUES
  )

export const ObservabilityToolAuthenticationProvider: React.FC<{
  initialValues?: Partial<ObservabilityToolAuthenticationContextValues>
}> = ({ initialValues, children }) => {
  const [isCreatingAuthenticationOnly, setIsCreatingAuthenticationOnly] =
    useBoolean(initialValues?.isCreatingAuthenticationOnly || false)
  const [authenticationResponse, setAuthenticationResponse] = useState(
    initialValues?.authenticationResponse || INITIAL_AUTHENTICATION_RESPONSE
  )
  const [isEdit, setIsEdit] = useBoolean(initialValues?.isEdit || false)
  const [isOnboarding, setIsOnboarding] = useBoolean(
    initialValues?.isOnboarding || false
  )

  const [isDrawerFlow, setIsDrawerFlow] = useBoolean(
    initialValues?.isDrawerFlow || false
  )

  const {
    data: awsIAMRoleMetadata,
    isFetching: isFetchingAwsIAMRoleMetadata,
    isError: hasErrorAwsIAMRoleMetadata,
    error: awsIAMRoleMetadataError,
    refetch: reFetchAwsIAMRoleMetadata
  } = useGremlinQuery<AWSIAMRoleMetadata>(
    'aws/metadata/iam-role',
    {},
    {
      initialData: {} as AWSIAMRoleMetadata,
      placeholderData: {} as AWSIAMRoleMetadata
    }
  )

  const value = useMemo(() => {
    return {
      authenticationResponse,
      setAuthenticationResponse,
      awsIAMRoleMetadata,
      isFetchingAwsIAMRoleMetadata,
      hasErrorAwsIAMRoleMetadata,
      awsIAMRoleMetadataError,
      reFetchAwsIAMRoleMetadata,
      isCreatingAuthenticationOnly,
      setIsCreatingAuthenticationOnly,
      isEdit,
      setIsEdit,
      isOnboarding,
      setIsOnboarding,
      isDrawerFlow,
      setIsDrawerFlow
    }
  }, [
    authenticationResponse,
    awsIAMRoleMetadata,
    awsIAMRoleMetadataError,
    hasErrorAwsIAMRoleMetadata,
    isCreatingAuthenticationOnly,
    isDrawerFlow,
    isEdit,
    isFetchingAwsIAMRoleMetadata,
    isOnboarding,
    reFetchAwsIAMRoleMetadata,
    setIsCreatingAuthenticationOnly,
    setIsDrawerFlow,
    setIsEdit,
    setIsOnboarding
  ])

  return (
    <ObservabilityToolAuthenticationContext.Provider value={value}>
      {children}
    </ObservabilityToolAuthenticationContext.Provider>
  )
}
